import { queryOptions, useQuery } from '@tanstack/react-query';

import type { InvoiceTemplate } from './types';
import type { Payload as PreviewInvoicePayload } from './usePreviewInvoiceTemplate';

import { apiResourceSchema, calculationDataSchema } from '../common/schemas';
import { fetchJson } from '../fetch';
import { QueryKeys } from '../keys';

type Payload = PreviewInvoicePayload;

const invoiceTemplateCalculationDataQueryOptions = (invoiceTemplate: InvoiceTemplate | null, body: Payload) => {
  // Fields that influence the calculation
  const dependencies = {
    contactId: body.clientContactId,
    lines: body.lines
      .filter((line) => line.type === 'entry')
      .map((line) => ({
        activityType: line.activityType,
        discountType: line.discountType,
        discountValue: line.discountValue,
        price: line.price,
        quantity: line.quantity,
        isPhotoAlbum: line.isPhotoAlbum,
      })),
  };

  return queryOptions({
    queryKey: QueryKeys.invoiceTemplates.show.calculation(invoiceTemplate?.id ?? 0, dependencies),
    queryFn: async () => {
      const { data: calculationData } = await fetchJson('/invoice-templates/calculate', {
        method: 'POST',
        schema: apiResourceSchema(calculationDataSchema),
        body,
      });

      return calculationData;
    },
    staleTime: Infinity, // Never refetch – calculation data never changes if dependencies remain unchanged
    gcTime: 0, // Instantly garbage collect – calculation data is no longer needed when query becomes inactive
    placeholderData: (previousData) => previousData ?? invoiceTemplate?.calculationData,
  });
};

export const useInvoiceTemplateCalculationData = (invoiceTemplate: InvoiceTemplate | null, body: Payload) => {
  return useQuery(invoiceTemplateCalculationDataQueryOptions(invoiceTemplate, body));
};
